import type { ReactNode } from "react"
import { useEffect, useRef } from "react"

import clsx from "clsx"
import { createPortal } from "react-dom"

/**
 * The overlay a lender application opens in on a public link.
 *
 * Deliberately not the seller `Modal`: that one is styled-components and pulls
 * the legacy styling stack into the public tree, which is Tailwind.
 *
 * A click on the backdrop does not close it. What it holds is a part-typed
 * credit application, and losing an SSN, an income and a co-applicant to a
 * stray click outside the card is not a trade worth making. Escape does close
 * it — a modal that traps keyboard users with no way out is worse — and so does
 * the form's own Cancel button.
 */
export const PublicApplicationDialog = ({
  label,
  onClose,
  children,
  aboveDrawer = false,
}: {
  label: string
  onClose: () => void
  children: ReactNode
  /**
   * Opened from a V2 `Drawer`: it layers above the drawer, and Escape closes
   * only the dialog, never the drawer under it.
   */
  aboveDrawer?: boolean
}): JSX.Element => {
  const dialog = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    dialog.current?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== `Escape`) return
      // The drawer listens on the document too; a capture listener runs first.
      if (aboveDrawer) event.stopImmediatePropagation()
      onClose()
    }
    document.addEventListener(`keydown`, onKeyDown, aboveDrawer)

    // The presentation behind the dialog should not scroll under it.
    const { overflow } = document.body.style
    document.body.style.overflow = `hidden`

    return () => {
      document.removeEventListener(`keydown`, onKeyDown, aboveDrawer)
      document.body.style.overflow = overflow
      opener?.focus?.()
    }
  }, [onClose, aboveDrawer])

  return createPortal(
    <div
      className={clsx(
        `fixed inset-0 flex items-center justify-center overflow-y-auto bg-slate-900/50 p-4`,
        // Above the V2 `Drawer`'s layer (z-[2147483000]), so a drawer step can open it.
        aboveDrawer ? `z-[2147483001]` : `z-50`,
      )}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl outline-none"
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
